import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Box, Stack, Table, TableBody, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { capitalize, flatten, last, map, sortBy, sum, sumBy, uniq, uniqBy } from 'lodash-es';
import React from 'react';
import NumberText, { formatNumber } from '~/components/NumberText';
import { formatUiDate, formatUiDateTime } from '~/lib/date';
import { calculateDailyEnergy } from '~/models/common';
import { calculateWeightedRatesByEnergyFromSegments } from '../weightedRateUtils';
import { calculateMeterConsumption } from './meterOfferCalculationUtils';
import type { ReportProjectMeter } from './reportTypes';
import { CalculatedProjectOffer, EAReportMeter } from './reportTypes';
import {
  NoPageBreakBefore,
  PageBreakBefore,
  Section,
  SectionContainer,
  StyledTableCell,
  StyledTableRow,
  Title,
  TitleUnderline
} from './style';

const OFFER_COLOR = [
  { bgColor: 'common.black', textColor: 'brand.main', textSecondaryColor: 'grey.A700' },
  { bgColor: 'grey.A700', textColor: 'common.white', textSecondaryColor: 'common.black' }
];

const BEST_OFFER_COLOR = { bgColor: 'brand.main', textColor: 'common.black', textSecondaryColor: 'common.white' };

/**
 * Multiple calendar segments (e.g. smooth/flat offers) can share identical TOU rates; that is not an "average" for display.
 * Show "Avg." only when segment rates actually differ for at least one of peak / off-peak / shoulder.
 */
function environmentalSegmentsHaveMultipleDistinctRates(
  segs: Array<{ peak?: number; offPeak?: number; shoulder?: number }>
): boolean {
  if (segs.length <= 1) return false;
  const peaks = segs.map((s) => s.peak ?? 0);
  const offs = segs.map((s) => s.offPeak ?? 0);
  const shs = segs.map((s) => s.shoulder ?? 0);
  return uniq(peaks).length > 1 || uniq(offs).length > 1 || uniq(shs).length > 1;
}

/** Segment row type for tooltip (exported for use in Offer Comparison). */
export type SegmentLike = {
  startDate?: string;
  endDate?: string;
  numOfDays?: number;
  peak?: number;
  offPeak?: number;
  shoulder?: number;
  lret?: number;
  sres?: number;
  ess?: number;
  veet?: number;
  aeeis?: number;
  pdrs?: number;
  reps?: number;
  /** Consumption (inc loss) kWh – for tooltip display */
  peakConsumptionIncLoss?: number;
  offPeakConsumptionIncLoss?: number;
  shoulderConsumptionIncLoss?: number;
  totalConsumptionIncLoss?: number;
};

/** Render segment details for tooltip (same structure as SummarySection). One or more groups; each group can have a meter label. Exported for Offer Comparison. */
export function renderSegmentsTooltipContent(
  groups: { meterLabel?: string; segments: SegmentLike[] }[],
  keyPrefix: string
): React.ReactNode {
  if (groups.length === 0 || groups.every((g) => !g.segments?.length)) return null;
  return (
    <Box sx={{ p: 1, maxHeight: '400px', overflowY: 'auto' }}>
      {groups.map((group, gIdx) =>
        (group.segments?.length ?? 0) > 0 ? (
          <Box key={`${keyPrefix}-${gIdx}`} sx={{ mb: gIdx < groups.length - 1 ? 2 : 0 }}>
            {group.meterLabel && (
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                {group.meterLabel}
              </Typography>
            )}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: group.meterLabel ? 'normal' : 'bold' }}>
              Segments Details ({group.segments.length} segments)
            </Typography>
            {group.segments.map((segment, idx) => (
              <Box
                key={idx}
                sx={{
                  mb: 1.5,
                  pb: 1.5,
                  borderBottom: idx < group.segments.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none'
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                  Segment {idx + 1}: {formatUiDate(segment.startDate)} - {formatUiDate(segment.endDate)} (
                  {segment.numOfDays} days)
                </Typography>
                <Box sx={{ pl: 1, fontSize: '0.75rem' }}>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    <strong>Contract Rates (c/kWh):</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', pl: 1 }}>
                    Peak: {formatNumber(segment.peak ?? 0, 4, '-')}, Off-Peak:{' '}
                    {formatNumber(segment.offPeak ?? 0, 4, '-')}, Shoulder:{' '}
                    {formatNumber(segment.shoulder ?? 0, 4, '-')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    <strong>Environmental Rates (c/kWh):</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', pl: 1 }}>
                    LRET: {formatNumber(segment.lret ?? 0, 4, '-')}, SRES: {formatNumber(segment.sres ?? 0, 4, '-')},
                    ESS: {formatNumber(segment.ess ?? 0, 4, '-')}, VEET: {formatNumber(segment.veet ?? 0, 4, '-')},
                    AEEIS: {formatNumber(segment.aeeis ?? 0, 4, '-')}, PDRS: {formatNumber(segment.pdrs ?? 0, 4, '-')},
                    REPS: {formatNumber(segment.reps ?? 0, 4, '-')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    <strong>Consumption kWh:</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', pl: 1 }}>
                    Peak: {formatNumber((segment as any).peakConsumptionExcludeLoss ?? 0, 0, '-')}, Off-Peak:{' '}
                    {formatNumber((segment as any).offPeakConsumptionExcludeLoss ?? 0, 0, '-')}, Shoulder:{' '}
                    {formatNumber((segment as any).shoulderConsumptionExcludeLoss ?? 0, 0, '-')}, Total:{' '}
                    {formatNumber((segment as any).totalConsumptionExcludeLoss ?? 0, 0, '-')}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ) : null
      )}
    </Box>
  );
}

const BestOfferSection: React.FC<{
  meters: EAReportMeter[];
  offers: CalculatedProjectOffer[];
  name: string;
  /** When true, show comparison block (TOTAL CONTESTABLE SPEND & ENERGY RATES + cards/table). When false (e.g. selected offers have different terms), hide it. */
  showComparison?: boolean;
  /** When provided (e.g. from merged group with per-meter selection), used for "Recommended Term" label. meterIds = which meters have this selection. */
  selectedTermEntries?: { retailerName: string; periodName: number; meterIds?: string[] }[];
  /** When true, show segment-detail help icon on table rows (preview only; hidden in print/share). */
  showSegmentHelp?: boolean;
}> = ({
  meters,
  offers,
  name,
  showComparison: showComparisonProp,
  selectedTermEntries: selectedTermEntriesProp,
  showSegmentHelp = false
}) => {
  const bestPeriodName = flatten(map(offers, 'periods')).find((period) => period.isBest)?.periodName;
  const showComparison = showComparisonProp ?? true;

  // Build "Recommended Term" label: single term → "12 months"; multiple terms → "Retailer A (12M) & Retailer B (24M)"
  // Prefer parent-provided selectedTermEntries (per-meter selection); fallback to isBest on offers (may only reflect one term in merged groups)
  const selectedTermEntries =
    selectedTermEntriesProp !== undefined
      ? selectedTermEntriesProp
      : uniqBy(
          offers.flatMap((offer) =>
            (offer.periods || [])
              .filter((p) => p.isBest)
              .map((p) => ({
                retailerName: (offer as { retailer?: { name?: string } }).retailer?.name ?? '?',
                periodName: p.periodName,
                meterIds: undefined
              }))
          ),
          (x) => `${x.retailerName} (${x.periodName}M)`
        );
  const recommendedTermLabel =
    selectedTermEntries.length === 0
      ? '—'
      : selectedTermEntries.length === 1
      ? `${selectedTermEntries[0].periodName} months`
      : selectedTermEntries.map((x) => `${x.retailerName} (${x.periodName}M)`).join(' & ');

  // Block (cards): only retailers with the recommended term. Tables: all retailers (no best-period filter).
  const offersForBlock =
    bestPeriodName != null
      ? offers.filter((offer) => (offer.periods || []).some((p) => p.periodName === bestPeriodName))
      : offers;
  const offersForTables = offers; // Tables show all retailers; (retailer, term) visibility already enforced in aggregateForDisplay.

  const sortedOffersForCards = sortBy(
    map(offersForBlock, (offer) => {
      const periodForTerm = offer.periods.find((p) => p.periodName === bestPeriodName) || offer.periods[0];
      return { ...offer, displayPeriod: periodForTerm };
    }),
    (o) => o.displayPeriod?.annualTotalCosts ?? 0
  );

  const getOfferColor = (index: number, isBest: boolean) => {
    return isBest ? BEST_OFFER_COLOR : OFFER_COLOR[index % 2];
  };

  type CardPeriod = {
    environmentalRatePeriods?: Array<{ endDate?: string; peak?: number; offPeak?: number; shoulder?: number }>;
    peak?: number;
    offPeak?: number;
    shoulder?: number;
    totalNumOfDays?: number;
    meterPeakRates?: Record<string, number>;
    meterOffPeakRates?: Record<string, number>;
    meterShoulderRates?: Record<string, number>;
  };
  /**
   * Card rate: when multiple offers/periods have different rates → weighted average; when same → last segment rate.
   * Mixed: weight by period.totalNumOfDays. Single with per-meter rates differing: weight by meter consumption.
   * Returns isWeightedAverage so the card can show "Avg." only when the displayed rate is actually an average.
   */
  const getCardRates = (
    periodOrRows: { period: CardPeriod }[] | CardPeriod,
    metersInGroup: EAReportMeter[]
  ): { peak: number; offPeak: number; shoulder: number; isWeightedAverage: boolean } => {
    const rows: { period: CardPeriod }[] = Array.isArray(periodOrRows) ? periodOrRows : [{ period: periodOrRows }];
    const periods: CardPeriod[] = rows.map((r) => r.period);
    const dailyPeak = sumBy(metersInGroup, (m) =>
      calculateDailyEnergy((m as ReportProjectMeter).annualData?.peakEnergy ?? 0)
    );
    const dailyOffPeak = sumBy(metersInGroup, (m) =>
      calculateDailyEnergy((m as ReportProjectMeter).annualData?.offPeakEnergy ?? 0)
    );
    const dailyShoulder = sumBy(metersInGroup, (m) =>
      calculateDailyEnergy((m as ReportProjectMeter).annualData?.shoulderEnergy ?? 0)
    );
    const getWeightedRateFromSegments = (p: typeof periods[0]) => {
      const segs = (p?.environmentalRatePeriods ?? []) as Array<{
        peak?: number;
        offPeak?: number;
        shoulder?: number;
        numOfDays?: number;
      }>;
      if (segs.length === 0) {
        return {
          peak: (p as { peak?: number }).peak ?? 0,
          offPeak: (p as { offPeak?: number }).offPeak ?? 0,
          shoulder: (p as { shoulder?: number }).shoulder ?? 0
        };
      }
      return calculateWeightedRatesByEnergyFromSegments(segs, dailyPeak, dailyOffPeak, dailyShoulder);
    };
    const weightedRates = periods.map(getWeightedRateFromSegments);
    const weightedPeakVals = weightedRates.map((r) => r.peak);
    const weightedOffPeakVals = weightedRates.map((r) => r.offPeak);
    const weightedShoulderVals = weightedRates.map((r) => r.shoulder);
    const ratesDiffer =
      uniq(weightedPeakVals).length > 1 ||
      uniq(weightedOffPeakVals).length > 1 ||
      uniq(weightedShoulderVals).length > 1;

    if (rows.length === 1 && periods[0]) {
      const p = periods[0];
      const meterRatesDiffer =
        p.meterPeakRates &&
        Object.keys(p.meterPeakRates).length > 0 &&
        uniq(Object.values(p.meterPeakRates)).length > 1;
      if (meterRatesDiffer && metersInGroup.length > 0 && (p.totalNumOfDays ?? 0) > 0) {
        const totalKwh = sumBy(metersInGroup, (m) =>
          calculateMeterConsumption(m as import('./reportTypes').ReportProjectMeter, p.totalNumOfDays ?? 0, {
            usePerMeterLossFactor: true
          })
        );
        if (totalKwh > 0) {
          const weightedPeakRate =
            sumBy(
              metersInGroup,
              (m) =>
                (p.meterPeakRates?.[m.id] ?? 0) *
                calculateMeterConsumption(m as import('./reportTypes').ReportProjectMeter, p.totalNumOfDays ?? 0, {
                  usePerMeterLossFactor: true
                })
            ) / totalKwh;
          const weightedOffPeakRate =
            sumBy(
              metersInGroup,
              (m) =>
                (p.meterOffPeakRates?.[m.id] ?? 0) *
                calculateMeterConsumption(m as import('./reportTypes').ReportProjectMeter, p.totalNumOfDays ?? 0, {
                  usePerMeterLossFactor: true
                })
            ) / totalKwh;
          const weightedShoulderRate =
            sumBy(
              metersInGroup,
              (m) =>
                (p.meterShoulderRates?.[m.id] ?? 0) *
                calculateMeterConsumption(m as import('./reportTypes').ReportProjectMeter, p.totalNumOfDays ?? 0, {
                  usePerMeterLossFactor: true
                })
            ) / totalKwh;
          return {
            peak: weightedPeakRate,
            offPeak: weightedOffPeakRate,
            shoulder: weightedShoulderRate,
            isWeightedAverage: true
          };
        }
      }
      const wr = getWeightedRateFromSegments(p);
      const weightedPeakRate = wr.peak;
      const weightedOffPeakRate = wr.offPeak;
      const weightedShoulderRate = wr.shoulder;
      const segs = (p?.environmentalRatePeriods ?? []) as Array<{ peak?: number; offPeak?: number; shoulder?: number }>;
      return {
        peak: weightedPeakRate,
        offPeak: weightedOffPeakRate,
        shoulder: weightedShoulderRate,
        isWeightedAverage: environmentalSegmentsHaveMultipleDistinctRates(segs)
      };
    }

    if (ratesDiffer && periods.length > 0) {
      const totalDays = sumBy(periods, (p) => p.totalNumOfDays ?? 0);
      if (totalDays > 0) {
        const weightedPeakRate =
          sum(periods.map((p, i) => weightedRates[i].peak * (p.totalNumOfDays ?? 0))) / totalDays;
        const weightedOffPeakRate =
          sum(periods.map((p, i) => weightedRates[i].offPeak * (p.totalNumOfDays ?? 0))) / totalDays;
        const weightedShoulderRate =
          sum(periods.map((p, i) => weightedRates[i].shoulder * (p.totalNumOfDays ?? 0))) / totalDays;
        return {
          peak: weightedPeakRate,
          offPeak: weightedOffPeakRate,
          shoulder: weightedShoulderRate,
          isWeightedAverage: true
        };
      }
    }

    const first = weightedRates[0];
    const firstPeriod = periods[0];
    const weightedPeakRate = first?.peak ?? (firstPeriod as { peak?: number })?.peak ?? 0;
    const weightedOffPeakRate = first?.offPeak ?? (firstPeriod as { offPeak?: number })?.offPeak ?? 0;
    const weightedShoulderRate = first?.shoulder ?? (firstPeriod as { shoulder?: number })?.shoulder ?? 0;
    const firstPeriodSegs = ((firstPeriod as { environmentalRatePeriods?: unknown[] })?.environmentalRatePeriods ??
      []) as Array<{ peak?: number; offPeak?: number; shoulder?: number }>;
    return {
      peak: weightedPeakRate,
      offPeak: weightedOffPeakRate,
      shoulder: weightedShoulderRate,
      isWeightedAverage: environmentalSegmentsHaveMultipleDistinctRates(firstPeriodSegs)
    };
  };

  /** When period has per-meter rates and they differ, show one line per meter; otherwise single value. */
  const renderRateCell = (
    period: {
      meterPeakRates?: Record<string, number>;
      meterOffPeakRates?: Record<string, number>;
      meterShoulderRates?: Record<string, number>;
    },
    rateKey: 'peak' | 'offPeak' | 'shoulder',
    singleVal: number
  ) => {
    const meterRates =
      rateKey === 'peak'
        ? period.meterPeakRates
        : rateKey === 'offPeak'
        ? period.meterOffPeakRates
        : period.meterShoulderRates;
    const values = meterRates ? Object.values(meterRates) : [];
    const showMultiLine = meterRates && uniq(values).length > 1 && Object.keys(meterRates).length > 0;
    if (showMultiLine && meterRates) {
      return (
        <Box component="span" sx={{ display: 'block' }}>
          {Object.entries(meterRates).map(([meterId, val]) => {
            const label = meters.find((m) => m.id === meterId)?.meterNumber ?? meterId;
            return (
              <Typography key={meterId} component="span" variant="body2" display="block">
                <NumberText value={val} precision={4} na="-" /> ({label})
              </Typography>
            );
          })}
        </Box>
      );
    }
    return <NumberText value={singleVal} precision={4} na="-" />;
  };

  // For multiple terms: resolve each selected (retailer, term) to (offer, period) for the summary table
  const selectedTermRows =
    selectedTermEntries.length > 1
      ? selectedTermEntries
          .map((entry) => {
            const offer = offers.find((o) => (o.retailer?.name ?? '?') === entry.retailerName);
            const period = offer?.periods?.find((p) => p.periodName === entry.periodName);
            return offer && period ? { offer, period, entry } : null;
          })
          .filter((x): x is NonNullable<typeof x> => x != null)
      : [];

  // Per-row costs for Mixed Offers table: only the meter(s) in that row (entry.meterIds), not full group.
  const getMixedOfferRowCosts = (
    period: typeof selectedTermRows[0]['period'],
    entry: typeof selectedTermRows[0]['entry']
  ) => {
    const meterIds = entry.meterIds?.length ? entry.meterIds : Object.keys(period.meterContestableCosts ?? {});
    const sumMeter = (rec: Record<string, number> | undefined) => sum(meterIds.map((id) => rec?.[id] ?? 0));
    const energy = sumMeter(period.meterEnergyCosts);
    const renewal = sumMeter(period.meterRenewableCosts);
    const retailerMetering = sumMeter(period.meterRetailerServiceFee) + sumMeter(period.meterMeteringCharge);
    const contestable = sumMeter(period.meterContestableCosts);
    const totalNumOfDays = period.totalNumOfDays ?? period.numOfDays ?? 0;
    const consumption =
      totalNumOfDays > 0 && meterIds.length > 0
        ? sumBy(
            meters.filter((m) => meterIds.includes(m.id)),
            (m) =>
              calculateMeterConsumption(m as import('./reportTypes').ReportProjectMeter, totalNumOfDays, {
                usePerMeterLossFactor: true
              })
          )
        : 0;
    const avgCentsPerKwh = consumption > 0 && contestable > 0 ? (contestable / consumption) * 100 : 0;
    return { energy, renewal, retailerMetering, contestable, consumption, avgCentsPerKwh };
  };

  // Totals and weighted average for Mixed offers table – sum of per-row values (only meters in each row).
  const selectedTotals =
    selectedTermRows.length > 0
      ? (() => {
          const rowCosts = selectedTermRows.map(({ period, entry }) => getMixedOfferRowCosts(period, entry));
          const totalEnergy = sum(rowCosts.map((r) => r.energy));
          const totalEnvironmental = sum(rowCosts.map((r) => r.renewal));
          const totalRetailerMetering = sum(rowCosts.map((r) => r.retailerMetering));
          const totalContestable = sum(rowCosts.map((r) => r.contestable));
          const totalConsumption = sum(rowCosts.map((r) => r.consumption));
          const weightedAvgCentsPerKwh = totalConsumption > 0 ? (totalContestable / totalConsumption) * 100 : 0;
          return {
            totalEnergy,
            totalEnvironmental,
            totalRetailerMetering,
            totalContestable,
            weightedAvgCentsPerKwh
          };
        })()
      : null;

  return (
    <>
      <Section sx={PageBreakBefore}>
        <SectionContainer sx={{ textAlign: 'center' }}>
          <Title variant="h4">BEST OFFERS</Title>
          <TitleUnderline />
          <Typography variant="h5">{name}</Typography>
          <Typography variant="caption" gutterBottom component="span">
            <i>{map(meters, 'meterNumber').filter(Boolean).join(', ') || '-'}</i>
          </Typography>
        </SectionContainer>
      </Section>

      <Section>
        <SectionContainer>
          <Typography variant="h6" gutterBottom>
            Recommended Term - {recommendedTermLabel}
          </Typography>
          <Typography variant="h6" color="#49BCE8" gutterBottom>
            TOTAL CONTESTABLE SPEND & ENERGY RATES
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            {showComparison && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 4,
                  flexWrap: 'wrap',
                  mt: 0,
                  justifyContent: 'center'
                }}
              >
                {selectedTermRows.length > 1
                  ? // Mixed offers (different retailers, same term): single block with all retailer names
                    (() => {
                      const retailerNames = selectedTermRows.map((r) => r.entry.retailerName).filter(Boolean);
                      const uniqueRetailers = [...new Set(retailerNames)];
                      const retailerLabel = uniqueRetailers.join(', ');
                      const totalPeriodContestable =
                        selectedTotals?.totalContestable ??
                        sum(selectedTermRows.map(({ period }) => period.periodContestableCosts ?? 0));
                      // When rates differ across offers → weighted avg by totalNumOfDays; when same → last segment rate
                      const {
                        peak: peakRate,
                        offPeak: offPeakRate,
                        shoulder: shoulderRate,
                        isWeightedAverage: showAvgLabel
                      } = getCardRates(selectedTermRows, meters);
                      return (
                        <Stack
                          key="best-offer-mixed"
                          sx={{
                            bgcolor: BEST_OFFER_COLOR.bgColor,
                            width: '24%',
                            minWidth: 200,
                            p: '0 20px 30px',
                            mr: '2%',
                            mt: 8,
                            textAlign: 'center',
                            pageBreakInside: 'avoid'
                          }}
                        >
                          <Box
                            sx={{
                              borderRadius: '100%',
                              bgcolor: BEST_OFFER_COLOR.bgColor,
                              width: 100,
                              height: 100,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: 'auto',
                              top: -40,
                              overflow: 'hidden',
                              position: 'relative',
                              transition: 'all 0.3s ease-in-out',
                              boxShadow: '0 0 0 8px #f0f0f0'
                            }}
                          >
                            <Typography variant="h5" color={BEST_OFFER_COLOR.textColor}>
                              1
                            </Typography>
                          </Box>

                          <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                            <TaskAltIcon fontSize="large" />
                            <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                              <Typography
                                variant="h6"
                                color={BEST_OFFER_COLOR.textColor}
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '100%'
                                }}
                                title={retailerLabel}
                              >
                                {retailerLabel || '—'}
                              </Typography>
                              <Typography variant="h6" color={BEST_OFFER_COLOR.textColor}>
                                $
                                <NumberText value={totalPeriodContestable} precision={0} />
                              </Typography>
                            </Box>
                          </Stack>
                          <br />
                          <Typography variant="subtitle2" color={BEST_OFFER_COLOR.textSecondaryColor}>
                            {peakRate > 0 && (
                              <>
                                {showAvgLabel && 'Avg.'}
                                &nbsp;Peak:&nbsp;
                                <NumberText value={peakRate} precision={4} />
                                &nbsp;c/kWh
                                <br />
                              </>
                            )}
                            {offPeakRate > 0 && (
                              <>
                                {showAvgLabel && 'Avg.'}
                                &nbsp;Off Peak:&nbsp;
                                <NumberText value={offPeakRate} precision={4} />
                                &nbsp;c/kWh
                                <br />
                              </>
                            )}
                            {shoulderRate > 0 && (
                              <>
                                {showAvgLabel && 'Avg.'}
                                &nbsp;Shoulder:&nbsp;
                                <NumberText value={shoulderRate} precision={4} />
                                &nbsp;c/kWh
                                <br />
                              </>
                            )}
                            <i>Mixed Offers (by term)</i>
                          </Typography>
                        </Stack>
                      );
                    })()
                  : sortedOffersForCards.map((offer, offerIndex) => {
                      const displayPeriod = offer.displayPeriod;
                      if (!displayPeriod) return null;
                      // When per-meter rates differ → weighted avg by consumption; when same → last segment rate
                      const {
                        peak: peakRate,
                        offPeak: offPeakRate,
                        shoulder: shoulderRate,
                        isWeightedAverage: showAvgLabel
                      } = getCardRates(displayPeriod, meters);

                      return (
                        <Stack
                          key={`best-offer-${map(meters, 'meterNumber').join('-')}-${offer.id}`}
                          sx={{
                            bgcolor: getOfferColor(offerIndex, offer.isBest).bgColor,
                            width: '24%',
                            p: '0 20px 30px',
                            mr: '2%',
                            mt: 8,
                            textAlign: 'center',
                            pageBreakInside: 'avoid'
                          }}
                        >
                          <Box
                            sx={{
                              borderRadius: '100%',
                              bgcolor: getOfferColor(offerIndex, offer.isBest).bgColor,
                              width: 100,
                              height: 100,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: 'auto',
                              top: -40,
                              overflow: 'hidden',
                              position: 'relative',
                              transition: 'all 0.3s ease-in-out',
                              boxShadow: '0 0 0 8px #f0f0f0'
                            }}
                          >
                            <Typography variant="h5" color={getOfferColor(offerIndex, offer.isBest).textColor}>
                              {offerIndex + 1}
                            </Typography>
                          </Box>

                          <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                            {offer.isBest && <TaskAltIcon fontSize="large" />}
                            <Box>
                              <Typography variant="h6" color={getOfferColor(offerIndex, offer.isBest).textColor}>
                                {offer.retailer.name}
                              </Typography>
                              <Typography variant="h6" color={getOfferColor(offerIndex, offer.isBest).textColor}>
                                $
                                <NumberText value={displayPeriod?.periodContestableCosts} precision={0} />
                              </Typography>
                            </Box>
                          </Stack>
                          <br />
                          <Typography
                            variant="subtitle2"
                            color={getOfferColor(offerIndex, offer.isBest).textSecondaryColor}
                          >
                            {peakRate > 0 && (
                              <>
                                {showAvgLabel && 'Avg.'}
                                &nbsp;Peak:&nbsp;
                                <NumberText value={peakRate} precision={4} />
                                &nbsp;c/kWh
                                <br />
                              </>
                            )}
                            {offPeakRate > 0 && (
                              <>
                                {showAvgLabel && 'Avg.'}
                                &nbsp;Off Peak:&nbsp;
                                <NumberText value={offPeakRate} precision={4} />
                                &nbsp;c/kWh
                                <br />
                              </>
                            )}
                            {shoulderRate > 0 && (
                              <>
                                {showAvgLabel && 'Avg.'}
                                &nbsp;Shoulder:&nbsp;
                                <NumberText value={shoulderRate} precision={4} />
                                &nbsp;c/kWh
                                <br />
                              </>
                            )}
                            <i>
                              Valid till{' '}
                              {offer.validityEndDateTime ? formatUiDateTime(offer.validityEndDateTime) : 'N/A'}
                            </i>
                          </Typography>
                          {offerIndex + 1 <= 3 && (
                            <Box sx={{ pageBreakAfter: 'always', clear: 'both', height: 0 }}></Box>
                          )}
                        </Stack>
                      );
                    })}
              </Box>
            )}
          </Box>

          {selectedTermRows.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Mixed Offers
              </Typography>
              <TableContainer sx={NoPageBreakBefore}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Meter</StyledTableCell>
                      <StyledTableCell>Retailer</StyledTableCell>
                      <StyledTableCell>Period</StyledTableCell>
                      <StyledTableCell>Rate Type</StyledTableCell>
                      <StyledTableCell colSpan={3}>kWh Commodity Unit Rate</StyledTableCell>
                      <StyledTableCell>Contract cost</StyledTableCell>
                      <StyledTableCell>Environmental Costs</StyledTableCell>
                      <StyledTableCell>Retailer & Metering Costs</StyledTableCell>
                      <StyledTableCell>Contestable Total Spend</StyledTableCell>
                      <StyledTableCell>Contestable Cost Per Unit</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ bgcolor: 'brand.main' }}>
                      <StyledTableCell sx={{ color: 'common.white' }}>—</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>—</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>—</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Offered</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Peak</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>OffPeak</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Shoulder</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Ex GST</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Ex GST</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Ex GST</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Ex GST</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>c/kWh</StyledTableCell>
                    </TableRow>
                    {selectedTermRows.map(({ period, entry }) => {
                      const rowCosts = getMixedOfferRowCosts(period, entry);
                      const envSeg = last(period?.environmentalRatePeriods ?? []) as
                        | { peak?: number; offPeak?: number; shoulder?: number }
                        | undefined;
                      const peakVal = envSeg?.peak ?? period.peak ?? 0;
                      const offPeakVal = envSeg?.offPeak ?? period.offPeak ?? 0;
                      const shoulderVal = envSeg?.shoulder ?? period.shoulder ?? 0;
                      const meterLabel =
                        entry.meterIds?.length &&
                        meters
                          .filter((m) => entry.meterIds!.includes(m.id))
                          .map((m) => m.meterNumber)
                          .filter(Boolean)
                          .join(', ');
                      return (
                        <StyledTableRow key={`${entry.retailerName}-${entry.periodName}`}>
                          <StyledTableCell component="th" scope="row">
                            {meterLabel || '—'}
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                              {entry.retailerName}
                              {showSegmentHelp &&
                                (() => {
                                  const segContent = renderSegmentsTooltipContent(
                                    [
                                      {
                                        meterLabel: meterLabel ? `Meters: ${meterLabel}` : undefined,
                                        segments: (period?.environmentalRatePeriods ?? []) as SegmentLike[]
                                      }
                                    ],
                                    `mixed-${entry.retailerName}-${entry.periodName}`
                                  );
                                  return segContent ? (
                                    <Tooltip title={segContent} arrow placement="right">
                                      <HelpOutlineIcon
                                        sx={{ fontSize: '0.875rem', color: 'text.secondary', cursor: 'help' }}
                                      />
                                    </Tooltip>
                                  ) : null;
                                })()}
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>{entry.periodName}M</StyledTableCell>
                          <StyledTableCell>
                            {capitalize(period.offerType === 'FLAT' ? 'SMOOTH' : period.offerType)}
                          </StyledTableCell>
                          <StyledTableCell>
                            <NumberText value={peakVal} precision={4} na="-" />
                          </StyledTableCell>
                          <StyledTableCell>
                            <NumberText value={offPeakVal} precision={4} na="-" />
                          </StyledTableCell>
                          <StyledTableCell>
                            <NumberText value={shoulderVal} precision={4} na="-" />
                          </StyledTableCell>
                          <StyledTableCell>
                            $<NumberText value={rowCosts.energy} precision={0} />
                          </StyledTableCell>
                          <StyledTableCell>
                            $<NumberText value={rowCosts.renewal} precision={0} />
                          </StyledTableCell>
                          <StyledTableCell>
                            $<NumberText value={rowCosts.retailerMetering} precision={0} />
                          </StyledTableCell>
                          <StyledTableCell>
                            $<NumberText value={rowCosts.contestable} precision={0} />
                          </StyledTableCell>
                          <StyledTableCell>
                            <NumberText value={rowCosts.avgCentsPerKwh} precision={2} />
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                    {selectedTotals && (
                      <TableRow sx={{ fontWeight: 'bold' }}>
                        <StyledTableCell colSpan={7} component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          Total / Weighted Average
                        </StyledTableCell>
                        <StyledTableCell>
                          $<NumberText value={selectedTotals.totalEnergy} precision={0} />
                        </StyledTableCell>
                        <StyledTableCell>
                          $<NumberText value={selectedTotals.totalEnvironmental} precision={0} />
                        </StyledTableCell>
                        <StyledTableCell>
                          $<NumberText value={selectedTotals.totalRetailerMetering} precision={0} />
                        </StyledTableCell>
                        <StyledTableCell>
                          $<NumberText value={selectedTotals.totalContestable} precision={0} />
                        </StyledTableCell>
                        <StyledTableCell>
                          <NumberText value={selectedTotals.weightedAvgCentsPerKwh} precision={2} />
                        </StyledTableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {offersForTables.map((offer, offerIndex) => (
            <Box key={offerIndex} sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {offer.retailer.name}
              </Typography>

              <TableContainer sx={NoPageBreakBefore}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>{offer.retailer.name}</StyledTableCell>
                      <StyledTableCell>Rate Type</StyledTableCell>
                      <StyledTableCell colSpan={3}>kWh Commodity Unit Rate</StyledTableCell>
                      <StyledTableCell>Contract cost</StyledTableCell>
                      <StyledTableCell>Environmental Costs</StyledTableCell>
                      <StyledTableCell>Retailer & Metering Costs</StyledTableCell>
                      <StyledTableCell>Contestable Total Spend</StyledTableCell>
                      <StyledTableCell>Contestable Cost Per Unit</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ bgcolor: 'brand.main' }}>
                      <StyledTableCell sx={{ color: 'common.white' }}>
                        Term
                        <br />
                        (Months)
                      </StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Offered</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Peak</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>OffPeak</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Shoulder</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Ex GST</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Ex GST</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Ex GST</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Ex GST</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>c/kWh</StyledTableCell>
                    </TableRow>
                    {map(offer.periods, (period) => {
                      const envSeg = last(period?.environmentalRatePeriods ?? []) as
                        | { peak?: number; offPeak?: number; shoulder?: number }
                        | undefined;
                      const peakVal = envSeg?.peak ?? period.peak ?? 0;
                      const offPeakVal = envSeg?.offPeak ?? period.offPeak ?? 0;
                      const shoulderVal = envSeg?.shoulder ?? period.shoulder ?? 0;
                      return (
                        <StyledTableRow
                          key={`${offer.retailer.name}-${period.periodName}`}
                          className={period.isBest === true ? 'highlight' : ''}
                        >
                          <StyledTableCell component="th" scope="row">
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                              {period.periodName}
                              {showSegmentHelp &&
                                (() => {
                                  const segContent = renderSegmentsTooltipContent(
                                    [
                                      {
                                        segments: (period?.environmentalRatePeriods ?? []) as SegmentLike[]
                                      }
                                    ],
                                    `retailer-${offer.retailer.name}-${period.periodName}`
                                  );
                                  return segContent ? (
                                    <Tooltip title={segContent} arrow placement="right">
                                      <HelpOutlineIcon
                                        sx={{ fontSize: '0.875rem', color: 'text.secondary', cursor: 'help' }}
                                      />
                                    </Tooltip>
                                  ) : null;
                                })()}
                            </Box>
                          </StyledTableCell>

                          <StyledTableCell>
                            {capitalize(period.offerType === 'FLAT' ? 'SMOOTH' : period.offerType)}
                          </StyledTableCell>
                          <StyledTableCell>{renderRateCell(period, 'peak', peakVal)}</StyledTableCell>
                          <StyledTableCell>{renderRateCell(period, 'offPeak', offPeakVal)}</StyledTableCell>
                          <StyledTableCell>{renderRateCell(period, 'shoulder', shoulderVal)}</StyledTableCell>
                          <StyledTableCell>
                            $<NumberText value={period.periodEnergyCosts} precision={0} />
                          </StyledTableCell>
                          <StyledTableCell>
                            $<NumberText value={period.periodRenewalCosts} precision={0} />
                          </StyledTableCell>
                          <StyledTableCell>
                            $
                            <NumberText
                              value={period.periodRetailerServiceFee + period.periodMeteringCharge}
                              precision={0}
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            $<NumberText value={period.periodContestableCosts} precision={0} />
                          </StyledTableCell>
                          <StyledTableCell>
                            <NumberText value={period.avgContestableCostsPerKwh} precision={2} />
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
        </SectionContainer>
      </Section>
    </>
  );
};

export default BestOfferSection;
